import axiosClient from './axiosClient';

/**
 * Typed axios wrappers for `/api/admin/globals/sweepstakes`.
 *
 * Same 3-phase versioning as tasks/adventures/cohorts. Sweepstakes adds:
 * - `start_date` / `end_date` — scheduling window (ISO date strings)
 * - `entry_cost` — integer (number of entries required)
 * - `prize_pool_json` — JSONB describing prize structure
 */

export interface SweepstakesHistoryRow {
  version: number;
  sweepstakes_name: string | null;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_cost: number | null;
  prize_pool_json: object | null;
  platform_sweepstakes_id: string | number | null;
  active: boolean;
  change_summary: string | null;
  change_type: 'create' | 'update' | 'delete' | 'publish' | 'rollback';
  created_at: string;
  created_by: string | null;
}

export interface AdminSweepstakesRow {
  sweepstakes_code: string;
  sweepstakes_name: string | null;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_cost: number | null;
  prize_pool_json: object | null;
  platform_sweepstakes_id: string | number | null;
  active: boolean;
  current_version: number;
  updated_at: string;
  updated_by: string | null;
  status: 'in_sync' | 'never_published' | 'unpublished';
}

export interface AdminSweepstakesDetail extends AdminSweepstakesRow {
  history: SweepstakesHistoryRow[];
}

export interface ListSweepstakesResponse {
  rows: AdminSweepstakesRow[];
}

export interface ListSweepstakesParams {
  sort?: 'sweepstakes_code' | 'sweepstakes_name' | 'updated_at' | 'current_version' | 'start_date';
  dir?: 'asc' | 'desc';
  status?: 'in_sync' | 'never_published';
}

export interface CreateSweepstakesPayload {
  sweepstakes_code: string;
  sweepstakes_name: string;
  description?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  entry_cost?: number | null;
  prize_pool_json?: object | null;
  change_summary?: string;
}

export interface UpdateSweepstakesPayload {
  base_version: number;
  sweepstakes_name?: string;
  description?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  entry_cost?: number | null;
  prize_pool_json?: object | null;
  active?: boolean;
  change_summary?: string;
}

export interface PublishSweepstakesEntityRequest {
  sweepstakes_code: string;
  base_version: number;
}

export interface PublishSweepstakesRequest {
  entities: PublishSweepstakesEntityRequest[];
  batch_id?: string;
  change_summary?: string;
}

export interface PublishSweepstakesEntityResult {
  sweepstakes_code: string;
  ok: boolean;
  published_version?: number;
  error?: string;
  message?: string;
  current_version?: number;
}

export interface PublishSweepstakesResponse {
  batch_id: string;
  results: PublishSweepstakesEntityResult[];
}

export interface RollbackSweepstakesPayload {
  target_version: number;
  base_version: number;
  change_summary?: string;
}

export const adminSweepstakesApi = {
  list: async (params: ListSweepstakesParams = {}): Promise<AdminSweepstakesRow[]> => {
    const res = await axiosClient.get<ListSweepstakesResponse>(
      '/api/admin/globals/sweepstakes',
      { params },
    );
    return res.data.rows;
  },

  get: async (code: string): Promise<AdminSweepstakesDetail> => {
    const res = await axiosClient.get<AdminSweepstakesDetail>(
      `/api/admin/globals/sweepstakes/${encodeURIComponent(code)}`,
    );
    return res.data;
  },

  create: async (payload: CreateSweepstakesPayload): Promise<AdminSweepstakesRow> => {
    const res = await axiosClient.post<AdminSweepstakesRow>(
      '/api/admin/globals/sweepstakes',
      payload,
    );
    return res.data;
  },

  update: async (
    code: string,
    payload: UpdateSweepstakesPayload,
  ): Promise<AdminSweepstakesRow> => {
    const res = await axiosClient.patch<AdminSweepstakesRow>(
      `/api/admin/globals/sweepstakes/${encodeURIComponent(code)}`,
      payload,
    );
    return res.data;
  },

  publish: async (
    payload: PublishSweepstakesRequest,
  ): Promise<PublishSweepstakesResponse> => {
    const res = await axiosClient.post<PublishSweepstakesResponse>(
      '/api/admin/globals/sweepstakes/publish',
      payload,
    );
    return res.data;
  },

  publishOne: async (
    code: string,
    base_version: number,
    options?: { batch_id?: string; change_summary?: string },
  ): Promise<PublishSweepstakesResponse> => {
    return adminSweepstakesApi.publish({
      entities: [{ sweepstakes_code: code, base_version }],
      batch_id: options?.batch_id,
      change_summary: options?.change_summary,
    });
  },

  rollback: async (
    code: string,
    payload: RollbackSweepstakesPayload,
  ): Promise<AdminSweepstakesRow> => {
    const res = await axiosClient.post<AdminSweepstakesRow>(
      `/api/admin/globals/sweepstakes/${encodeURIComponent(code)}/rollback`,
      payload,
    );
    return res.data;
  },
};
